import { useCallback, useEffect, useMemo, useState } from "react";
import {
  alfredService,
  AlfredSupportedOption,
  AlfredSupportedOptionsResponse,
} from "src/api";
import { useUrlParamsContext } from "src/contexts";

interface UseAlfredSupportedOptionsParams {
  type: "deposit" | "withdrawal";
}

/**
 * Loads the per-route currency options on mount and exposes a
 * controlled selection (`selected`, `setSelected`) the form can bind to.
 *
 * Default selection is the first option — the form can override it
 * when the user's KYC country matches one of the listed currencies
 * (we expose `selectByCurrency` for that case).
 *
 * `raiseMinAmount` lets the deposit form lift a stale cached min when a
 * live Alfred quote rejects a lower amount (configs cache can lag the
 * real pair limits by up to the BE TTL).
 */
export const useAlfredSupportedOptions = (
  params: UseAlfredSupportedOptionsParams,
) => {
  const { paymentRouteId } = useUrlParamsContext();
  const [response, setResponse] =
    useState<AlfredSupportedOptionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  /** Per-currency floors discovered from live quote rejections. */
  const [minOverrides, setMinOverrides] = useState<Record<string, number>>({});

  // A learned live floor belongs to one payment route only. Do not carry,
  // for example, a 50 BRL override into another BRL route whose min is 25.
  useEffect(() => {
    setMinOverrides({});
  }, [paymentRouteId, params.type]);

  const loadOptions = useCallback(async () => {
    if (!paymentRouteId) return null;
    return alfredService.getSupportedOptions({
      paymentRouteId,
      type: params.type,
    });
  }, [paymentRouteId, params.type]);

  useEffect(() => {
    if (!paymentRouteId) return;
    let cancelled = false;
    setLoading(true);
    loadOptions()
      .then((data) => {
        if (cancelled || !data) return;
        setResponse(data);
        if (data.options.length && !selectedCurrency) {
          setSelectedCurrency(data.options[0].source_currency);
        }
      })
      .catch((err) => {
        console.error("Failed to load Alfred supported options:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // selectedCurrency intentionally excluded so we don't reset on every
    // change; we only seed the default once the response arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRouteId, params.type, loadOptions]);

  const refetch = useCallback(async () => {
    if (!paymentRouteId) return;
    try {
      const data = await loadOptions();
      if (data) setResponse(data);
    } catch (err) {
      console.error("Failed to refresh Alfred supported options:", err);
    }
  }, [loadOptions, paymentRouteId]);

  /**
   * Raise the displayed/enforced min for a currency when Alfred's live
   * quote proves our cached `min_amount` is too low. Never lowers it.
   */
  const raiseMinAmount = useCallback((currency: string, min: number) => {
    if (!currency || !Number.isFinite(min) || min <= 0) return;
    const key = currency.toUpperCase();
    setMinOverrides((prev) => {
      const current = prev[key] ?? 0;
      if (min <= current) return prev;
      return { ...prev, [key]: min };
    });
  }, []);

  const selected: AlfredSupportedOption | null = useMemo(() => {
    if (!response || !selectedCurrency) return null;
    const base =
      response.options.find((o) => o.source_currency === selectedCurrency) ??
      null;
    if (!base) return null;
    const override = minOverrides[base.source_currency.toUpperCase()];
    const cached = Number(base.min_amount);
    if (
      override != null &&
      Number.isFinite(override) &&
      (!Number.isFinite(cached) || override > cached)
    ) {
      return { ...base, min_amount: String(override) };
    }
    return base;
  }, [response, selectedCurrency, minOverrides]);

  const options = useMemo(() => {
    if (!response) return [];
    return response.options.map((opt) => {
      const override = minOverrides[opt.source_currency.toUpperCase()];
      const cached = Number(opt.min_amount);
      if (
        override != null &&
        Number.isFinite(override) &&
        (!Number.isFinite(cached) || override > cached)
      ) {
        return { ...opt, min_amount: String(override) };
      }
      return opt;
    });
  }, [response, minOverrides]);

  return {
    options,
    selected,
    selectedCurrency,
    setSelectedCurrency,
    loading,
    refetch,
    raiseMinAmount,
  };
};
